import { useEffect } from 'react';
import { FormControl, Input } from 'components/ui';
import { Flex, useBreakpointValue } from '@chakra-ui/react';
import SignUpInfoLocation from '../../OnboardingForm/SignUpInfoLocation';
import { Address } from '../types';

type Props = {
  address: Address | undefined;
  onChange: (address: Address) => void;
};

export const LongFormAddress = ({ address, onChange }: Props) => {
  const { street, street2, city, state, zip } = address || {};

  // The lookup leaves the zip blank when it finds none, which disables the CTA
  // with nothing on screen to say why. Only flag it once the zip is the one
  // thing still missing, so a blank manual form and the moment before the
  // lookup fills the fields in stay quiet.
  const isZipMissing = !zip && !!street && !!city && !!state;

  const isMobile = useBreakpointValue({
    base: true,
    lg: false,
  });

  useEffect(() => {
    if (!address) return;

    onChange(address);
    // we only want it running when address changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  return (
    <>
      <FormControl required>
        <FormControl.Label>Street address</FormControl.Label>

        <Input
          w={'100%'}
          maxW={'100%'}
          type="text"
          value={street}
          onChange={(e) => {
            onChange({
              ...address,
              street: e.target.value,
            });
          }}
        />
      </FormControl>

      <FormControl>
        <FormControl.Label>Street address 2</FormControl.Label>

        <Input
          w={'100%'}
          maxW={'100%'}
          type="text"
          value={street2}
          onChange={(e) => {
            onChange({
              ...address,
              street2: e.target.value,
            });
          }}
        />
      </FormControl>

      <FormControl required>
        <FormControl.Label>City</FormControl.Label>

        <Input
          w={'100%'}
          maxW={'100%'}
          type="text"
          value={city}
          onChange={(e) => {
            onChange({
              ...address,
              city: e.target.value,
            });
          }}
        />
      </FormControl>

      <Flex
        direction={isMobile ? 'column' : 'row'}
        gap={isMobile ? '18px' : '12px'}
        align={isMobile ? undefined : 'stretch'}
        w="100%"
      >
        <Flex direction="column" justify="flex-end" flex={1} minW={0}>
          <SignUpInfoLocation
            value={{
              value: state,
              label: state,
            }}
            dispatch={(selection) => {
              onChange({
                ...address,
                state: selection.payload,
              });
            }}
          />
        </Flex>

        <Flex direction="column" justify="flex-end" flex={1} minW={0}>
          <FormControl required invalid={isZipMissing} position="relative">
            <FormControl.Label>Zip Code</FormControl.Label>

            <Input
              w={'100%'}
              maxW={'100%'}
              type="text"
              value={zip}
              onChange={(e) => {
                onChange({
                  ...address,
                  zip: e.target.value,
                });
              }}
              data-testid="address-zip-input"
            />

            {/* Out of flow, so the message does not make this column taller
                and knock the field out of line with the state dropdown. */}
            <FormControl.ErrorMessage
              position="absolute"
              top="100%"
              left={0}
              mt="2px"
            >
              Add your zip code to continue
            </FormControl.ErrorMessage>
          </FormControl>
        </Flex>
      </Flex>
    </>
  );
};

export default LongFormAddress;
